import type { Decision } from '../game/data/types';

/**
 * Hand-authored canvas art for the physical GREENLIT / SUNSET rubber stamps.
 * The desk scene is a flat orthographic stage with unlit MeshBasicMaterial, so
 * every bit of depth (bevels, sheen, grounding shadow) is painted into the
 * texture rather than lit. Silhouette first: turned-wood knob, brushed-metal
 * ferrule, wood mount with an engraved nameplate, colored ink die at the base.
 */

export const STAMP_ART_W = 320;
export const STAMP_ART_H = 452;

interface Palette {
  label: string;
  inkBright: string;
  inkMid: string;
  inkDeep: string;
  accent: string;
  glow: string;
  plateText: string;
}

const PALETTES: Record<Decision, Palette> = {
  greenlit: {
    label: 'GREENLIT',
    inkBright: '#74d67f',
    inkMid: '#3f9e52',
    inkDeep: '#215f30',
    accent: '#63c46e',
    glow: 'rgba(78,196,104,0.55)',
    plateText: '#8ee49a',
  },
  sunset: {
    label: 'SUNSET',
    inkBright: '#f0b45c',
    inkMid: '#c0533a',
    inkDeep: '#722619',
    accent: '#e07a48',
    glow: 'rgba(214,96,60,0.55)',
    plateText: '#f0a878',
  },
};

// Warm walnut wood ramp, shared by both stamps.
const WOOD_LIGHT = '#b98246';
const WOOD_MID = '#835429';
const WOOD_DARK = '#4d2f14';
// Brushed steel ramp for the ferrule.
const METAL_LIGHT = '#eef2f6';
const METAL_MID = '#a3abb4';
const METAL_EDGE = '#31363c';

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  r: number | [number, number, number, number],
): void {
  const [tl, tr, br, bl] = typeof r === 'number' ? [r, r, r, r] : r;
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  ctx.arcTo(x + w, y, x + w, y + tr, tr);
  ctx.lineTo(x + w, y + h - br);
  ctx.arcTo(x + w, y + h, x + w - br, y + h, br);
  ctx.lineTo(x + bl, y + h);
  ctx.arcTo(x, y + h, x, y + h - bl, bl);
  ctx.lineTo(x, y + tl);
  ctx.arcTo(x, y, x + tl, y, tl);
  ctx.closePath();
}

/** Builds a high-resolution canvas of the whole physical stamp for `kind`. */
export function buildStampCanvas(kind: Decision): HTMLCanvasElement {
  const p = PALETTES[kind];
  const canvas = document.createElement('canvas');
  const scale = 2; // supersample for crisp bevels when the plane is scaled up
  canvas.width = STAMP_ART_W * scale;
  canvas.height = STAMP_ART_H * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  const W = STAMP_ART_W;
  const cx = W / 2;

  // ---- grounding shadow -------------------------------------------------
  ctx.save();
  const shadow = ctx.createRadialGradient(cx, 424, 6, cx, 424, 120);
  shadow.addColorStop(0, 'rgba(0,0,0,0.5)');
  shadow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = shadow;
  ctx.beginPath();
  ctx.ellipse(cx, 424, 118, 26, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // ---- ink glow under the die ------------------------------------------
  ctx.save();
  const glow = ctx.createRadialGradient(cx, 392, 8, cx, 392, 130);
  glow.addColorStop(0, p.glow);
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.ellipse(cx, 396, 120, 40, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // ---- turned-wood knob -------------------------------------------------
  const knobCy = 82;
  const knobR = 62;
  const knob = ctx.createRadialGradient(cx - 22, knobCy - 24, 6, cx, knobCy, knobR + 10);
  knob.addColorStop(0, '#d6a763');
  knob.addColorStop(0.35, WOOD_LIGHT);
  knob.addColorStop(0.72, WOOD_MID);
  knob.addColorStop(1, WOOD_DARK);
  ctx.fillStyle = knob;
  ctx.beginPath();
  ctx.ellipse(cx, knobCy, knobR, knobR - 4, 0, 0, Math.PI * 2);
  ctx.fill();
  // grain rings
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(cx, knobCy, knobR, knobR - 4, 0, 0, Math.PI * 2);
  ctx.clip();
  ctx.strokeStyle = 'rgba(60,34,12,0.25)';
  ctx.lineWidth = 1.4;
  for (let i = 1; i <= 4; i++) {
    ctx.beginPath();
    ctx.ellipse(cx + 6, knobCy + 6, knobR * (0.32 + i * 0.16), (knobR - 4) * (0.3 + i * 0.16), -0.2, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
  // specular highlight
  ctx.save();
  const hl = ctx.createRadialGradient(cx - 24, knobCy - 26, 2, cx - 24, knobCy - 26, 40);
  hl.addColorStop(0, 'rgba(255,246,224,0.85)');
  hl.addColorStop(1, 'rgba(255,246,224,0)');
  ctx.fillStyle = hl;
  ctx.beginPath();
  ctx.ellipse(cx - 22, knobCy - 24, 30, 20, -0.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // rim shade
  ctx.strokeStyle = 'rgba(40,24,8,0.5)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(cx, knobCy, knobR, knobR - 4, 0, 0, Math.PI * 2);
  ctx.stroke();

  // ---- neck (knob into ferrule) ----------------------------------------
  const neck = ctx.createLinearGradient(cx - 60, 0, cx + 60, 0);
  neck.addColorStop(0, WOOD_DARK);
  neck.addColorStop(0.22, WOOD_MID);
  neck.addColorStop(0.5, WOOD_LIGHT);
  neck.addColorStop(0.78, WOOD_MID);
  neck.addColorStop(1, WOOD_DARK);
  ctx.fillStyle = neck;
  ctx.beginPath();
  ctx.moveTo(cx - 36, 132);
  ctx.lineTo(cx + 36, 132);
  ctx.lineTo(cx + 58, 168);
  ctx.lineTo(cx - 58, 168);
  ctx.closePath();
  ctx.fill();

  // ---- brushed-metal ferrule -------------------------------------------
  const bandY = 160;
  const bandH = 50;
  const bandX = cx - 70;
  const bandW = 140;
  roundRect(ctx, bandX, bandY, bandW, bandH, 11);
  const band = ctx.createLinearGradient(bandX, 0, bandX + bandW, 0);
  band.addColorStop(0, METAL_EDGE);
  band.addColorStop(0.08, METAL_LIGHT);
  band.addColorStop(0.3, METAL_MID);
  band.addColorStop(0.52, METAL_LIGHT);
  band.addColorStop(0.74, METAL_MID);
  band.addColorStop(0.94, METAL_LIGHT);
  band.addColorStop(1, METAL_EDGE);
  ctx.fillStyle = band;
  ctx.fill();
  // top bevel + bottom shade + rib grooves
  ctx.save();
  roundRect(ctx, bandX, bandY, bandW, bandH, 11);
  ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(bandX + 4, bandY + 3); ctx.lineTo(bandX + bandW - 4, bandY + 3); ctx.stroke();
  ctx.strokeStyle = 'rgba(0,0,0,0.45)';
  ctx.beginPath(); ctx.moveTo(bandX + 4, bandY + bandH - 3); ctx.lineTo(bandX + bandW - 4, bandY + bandH - 3); ctx.stroke();
  for (const gy of [bandY + 15, bandY + bandH - 15]) {
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.beginPath(); ctx.moveTo(bandX + 6, gy); ctx.lineTo(bandX + bandW - 6, gy); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.beginPath(); ctx.moveTo(bandX + 6, gy + 1.5); ctx.lineTo(bandX + bandW - 6, gy + 1.5); ctx.stroke();
  }
  ctx.restore();

  // ---- wood mount block -------------------------------------------------
  const mX = cx - 84;
  const mW = 168;
  const mY = 204;
  const mH = 150;
  roundRect(ctx, mX, mY, mW, mH, [6, 6, 16, 16]);
  const mount = ctx.createLinearGradient(0, mY, 0, mY + mH);
  mount.addColorStop(0, WOOD_LIGHT);
  mount.addColorStop(0.5, WOOD_MID);
  mount.addColorStop(1, WOOD_DARK);
  ctx.fillStyle = mount;
  ctx.fill();
  // side vignette so the block reads as a rounded volume
  ctx.save();
  roundRect(ctx, mX, mY, mW, mH, [6, 6, 16, 16]);
  ctx.clip();
  const sideShade = ctx.createLinearGradient(mX, 0, mX + mW, 0);
  sideShade.addColorStop(0, 'rgba(20,12,4,0.55)');
  sideShade.addColorStop(0.2, 'rgba(20,12,4,0)');
  sideShade.addColorStop(0.8, 'rgba(20,12,4,0)');
  sideShade.addColorStop(1, 'rgba(20,12,4,0.55)');
  ctx.fillStyle = sideShade;
  ctx.fillRect(mX, mY, mW, mH);
  // subtle wood grain streaks
  ctx.strokeStyle = 'rgba(58,32,12,0.22)';
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 6; i++) {
    const gx = mX + 14 + i * 26;
    ctx.beginPath();
    ctx.moveTo(gx, mY + 4);
    ctx.bezierCurveTo(gx + 5, mY + 50, gx - 5, mY + 100, gx + 3, mY + mH - 4);
    ctx.stroke();
  }
  ctx.restore();
  // top highlight line + colored accent bevel just under the ferrule
  ctx.strokeStyle = 'rgba(255,240,214,0.4)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(mX + 8, mY + 2); ctx.lineTo(mX + mW - 8, mY + 2); ctx.stroke();
  ctx.fillStyle = p.accent;
  roundRect(ctx, mX + 10, mY + 8, mW - 20, 5, 2);
  ctx.fill();

  // ---- engraved nameplate ----------------------------------------------
  const plX = cx - 66;
  const plW = 132;
  const plY = 238;
  const plH = 62;
  roundRect(ctx, plX, plY, plW, plH, 8);
  ctx.fillStyle = '#1c2026';
  ctx.fill();
  // recessed bevel: dark top-left, light bottom-right
  ctx.save();
  roundRect(ctx, plX, plY, plW, plH, 8);
  ctx.clip();
  ctx.strokeStyle = 'rgba(0,0,0,0.7)';
  ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(plX + 2, plY + plH - 2); ctx.lineTo(plX + 2, plY + 2); ctx.lineTo(plX + plW - 2, plY + 2); ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.beginPath(); ctx.moveTo(plX + plW - 2, plY + 2); ctx.lineTo(plX + plW - 2, plY + plH - 2); ctx.lineTo(plX + 2, plY + plH - 2); ctx.stroke();
  ctx.restore();
  // engraved text: dark cut + colored top light for emboss
  const fontSize = p.label.length > 6 ? 25 : 30;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `700 ${fontSize}px "Arial Narrow", "Segoe UI", Arial, sans-serif`;
  ctx.save();
  roundRect(ctx, plX + 3, plY + 3, plW - 6, plH - 6, 6);
  ctx.clip();
  ctx.fillStyle = 'rgba(0,0,0,0.85)';
  ctx.fillText(p.label, cx + 1, plY + plH / 2 + 2);
  ctx.fillStyle = p.plateText;
  ctx.fillText(p.label, cx, plY + plH / 2);
  ctx.restore();

  // ---- inked rubber die at the base ------------------------------------
  const dX = cx - 72;
  const dW = 144;
  const dY = 352;
  const dH = 46;
  roundRect(ctx, dX, dY, dW, dH, [4, 4, 12, 12]);
  const die = ctx.createLinearGradient(0, dY, 0, dY + dH);
  die.addColorStop(0, p.inkBright);
  die.addColorStop(0.45, p.inkMid);
  die.addColorStop(1, p.inkDeep);
  ctx.fillStyle = die;
  ctx.fill();
  // glossy top line + darker press rim at the very bottom
  ctx.save();
  roundRect(ctx, dX, dY, dW, dH, [4, 4, 12, 12]);
  ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(dX + 8, dY + 3); ctx.lineTo(dX + dW - 8, dY + 3); ctx.stroke();
  ctx.fillStyle = 'rgba(0,0,0,0.32)';
  ctx.fillRect(dX, dY + dH - 8, dW, 8);
  ctx.restore();

  return canvas;
}
